<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required, email, between, maxLength } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { STORE_MODULE } from "@/state/modules/store";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { storeMethods, lookupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

const STORE_TYPES = [
  { value: "direct", labelKey: "store.type.direct" },
  { value: "dealer", labelKey: "store.type.dealer" },
];

export default {
  page() {
    return {
      title: this.isEdit
        ? i18n.t("label.updateX", [i18n.t("module.store")])
        : i18n.t("label.createX", [i18n.t("module.store")]),
    };
  },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      form: {
        name: "",
        store_type: "direct",
        debtor_code: "",
        address: "",
        city: "",
        state: "",
        postcode: "",
        phone: "",
        email: "",
        latitude: null,
        longitude: null,
        operating_hours: "",
        is_active: true,
      },
      // Operating hours, split into open/close time (composed into operating_hours).
      openHour: "09",
      openMin: "00",
      closeHour: "22",
      closeMin: "00",
      submitting: false,
      selectedStatus: null,
      selectedType: null,
      selectedDebtor: null,
    };
  },
  validations() {
    return {
      form: {
        name: { required, maxLength: maxLength(150) },
        email: { email },
        address: { required },
        city: { required },
        state: { required },
        postcode: { required },
        latitude: { required, between: between(-90, 90) },
        longitude: { required, between: between(-180, 180) },
        debtor_code: this.form.store_type === "dealer" ? { required } : {},
      },
    };
  },
  computed: {
    ...mapState(STORE_MODULE, ["stores"]),
    ...mapState(LOOKUP_MODULE, ["debtorDdl"]),
    typeOptions() {
      return STORE_TYPES.map((t) => ({ value: t.value, text: i18n.t(t.labelKey) }));
    },
    hourOptions() {
      return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
    },
    minuteOptions() {
      return Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
    },
    isEdit() { return !!this.$route.params.uuid; },
    title() {
      return this.isEdit
        ? i18n.t("label.updateX", [i18n.t("module.store")])
        : i18n.t("label.createX", [i18n.t("module.store")]);
    },
    items() {
      return [
        { text: i18n.t("module.customer"), href: "/" },
        { text: i18n.t("module.store"), href: "/stores" },
        { text: this.title, active: true },
      ];
    },
    statusOptions() {
      return [
        { value: true, text: i18n.t("label.active") },
        { value: false, text: i18n.t("label.inactive") },
      ];
    },
  },
  async created() {
    await Promise.all([this.getAllStores(), this.lookupDebtor({})]);
    if (this.isEdit) {
      const row = (this.stores.data || []).find(
        (s) => String(s.uuid) === String(this.$route.params.uuid)
      );
      if (row) {
        this.form = {
          name: row.name || "",
          store_type: row.storeType || "direct",
          debtor_code: row.debtorCode || "",
          address: row.address || "",
          city: row.city || "",
          state: row.state || "",
          postcode: row.postcode || "",
          phone: row.phone || "",
          email: row.email || "",
          latitude: row.latitude,
          longitude: row.longitude,
          operating_hours: row.operatingHours || "",
          is_active: row.isActive,
        };
        // Parse "HH:MM - HH:MM" back into the open/close selectors.
        const m = (row.operatingHours || "").match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
        if (m) {
          this.openHour = m[1].padStart(2, "0");
          this.openMin = m[2];
          this.closeHour = m[3].padStart(2, "0");
          this.closeMin = m[4];
        }
      }
    }
    this.selectedStatus =
      this.statusOptions.find((s) => s.value === this.form.is_active) || this.statusOptions[0];
    this.selectedType =
      this.typeOptions.find((t) => t.value === this.form.store_type) || this.typeOptions[0];
    this.selectedDebtor =
      (this.debtorDdl || []).find((d) => d.account_no === this.form.debtor_code) || null;
  },
  methods: {
    ...storeMethods,
    ...lookupMethods,
    onStatusChange(s) {
      this.selectedStatus = s;
      this.form.is_active = s ? s.value : true;
    },
    onTypeChange(t) {
      this.selectedType = t;
      this.form.store_type = t ? t.value : "direct";
      if (this.form.store_type !== "dealer") {
        this.form.debtor_code = "";
        this.selectedDebtor = null;
      }
    },
    onDebtorChange(d) {
      this.selectedDebtor = d;
      this.form.debtor_code = d ? d.account_no : "";
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.form.operating_hours = `${this.openHour}:${this.openMin} - ${this.closeHour}:${this.closeMin}`;
      this.submitting = true;
      try {
        let result;
        if (this.isEdit) {
          result = await this.updateStore({
            uuid: this.$route.params.uuid,
            data: this.form,
          });
        } else {
          result = await this.createStore({ data: this.form });
        }
        if (result) this.$router.push({ name: "Store-listing" });
      } finally {
        this.submitting = false;
      }
    },
    cancel() {
      this.$router.push({ name: "Store-listing" });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('label.name')">
                    <b-form-input
                      v-model="form.name"
                      :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
                    />
                    <small v-if="$v.form.name.$dirty && $v.form.name.$error" class="text-danger">
                      {{ $t('message.requiredX', [$t('label.name')]) }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('store.type.label')">
                    <multiselect
                      :value="selectedType"
                      :options="typeOptions"
                      track-by="value"
                      label="text"
                      open-direction="below"
                      :show-labels="false"
                      :allow-empty="false"
                      :placeholder="$t('label.select')"
                      @input="onTypeChange"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" v-if="form.store_type === 'dealer'">
                  <b-form-group :label="$t('store.debtor')">
                    <multiselect
                      :value="selectedDebtor"
                      :options="debtorDdl"
                      track-by="account_no"
                      :custom-label="(d) => `${d.account_no} — ${d.company_name || ''}`"
                      open-direction="below"
                      :show-labels="false"
                      :allow-empty="false"
                      :placeholder="$t('label.select')"
                      @input="onDebtorChange"
                    />
                    <small v-if="$v.form.debtor_code.$dirty && $v.form.debtor_code.$error" class="text-danger">
                      {{ $t('message.requiredX', [$t('store.debtor')]) }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.address')">
                    <b-form-textarea
                      v-model="form.address"
                      rows="2"
                      :state="$v.form.address.$dirty ? !$v.form.address.$error : null"
                    />
                    <small v-if="$v.form.address.$dirty && $v.form.address.$error" class="text-danger">
                      {{ $t('message.requiredX', [$t('label.address')]) }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="4">
                  <b-form-group :label="$t('label.city')">
                    <b-form-input
                      v-model="form.city"
                      :state="$v.form.city.$dirty ? !$v.form.city.$error : null"
                    />
                    <small v-if="$v.form.city.$dirty && $v.form.city.$error" class="text-danger">
                      {{ $t('message.requiredX', [$t('label.city')]) }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="4">
                  <b-form-group :label="$t('label.state')">
                    <b-form-input
                      v-model="form.state"
                      :state="$v.form.state.$dirty ? !$v.form.state.$error : null"
                    />
                    <small v-if="$v.form.state.$dirty && $v.form.state.$error" class="text-danger">
                      {{ $t('message.requiredX', [$t('label.state')]) }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="4">
                  <b-form-group :label="$t('label.postcode')">
                    <b-form-input
                      v-model="form.postcode"
                      :state="$v.form.postcode.$dirty ? !$v.form.postcode.$error : null"
                    />
                    <small v-if="$v.form.postcode.$dirty && $v.form.postcode.$error" class="text-danger">
                      {{ $t('message.requiredX', [$t('label.postcode')]) }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.phone')">
                    <b-form-input v-model="form.phone" />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.email')">
                    <b-form-input
                      type="email"
                      v-model="form.email"
                      :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
                    />
                    <small v-if="$v.form.email.$dirty && $v.form.email.$error" class="text-danger">
                      {{ $t('message.invalidX', [$t('label.email')]) }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.latitude')">
                    <b-form-input
                      type="number"
                      step="0.0000001"
                      v-model.number="form.latitude"
                      :state="$v.form.latitude.$dirty ? !$v.form.latitude.$error : null"
                    />
                    <small v-if="$v.form.latitude.$dirty && $v.form.latitude.$error" class="text-danger">
                      {{ $t('message.invalidX', [$t('label.latitude')]) }} (-90 ~ 90)
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.longitude')">
                    <b-form-input
                      type="number"
                      step="0.0000001"
                      v-model.number="form.longitude"
                      :state="$v.form.longitude.$dirty ? !$v.form.longitude.$error : null"
                    />
                    <small v-if="$v.form.longitude.$dirty && $v.form.longitude.$error" class="text-danger">
                      {{ $t('message.invalidX', [$t('label.longitude')]) }} (-180 ~ 180)
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.operatingHours')">
                    <b-row>
                      <b-col cols="12" md="6">
                        <small class="text-muted d-block mb-1">{{ $t('store.open') }}</small>
                        <div class="d-flex align-items-center" style="gap: 6px;">
                          <multiselect :value="openHour" :options="hourOptions" open-direction="below" :show-labels="false" :allow-empty="false" placeholder="HH" @input="(v) => openHour = v" />
                          <span class="font-weight-bold">:</span>
                          <multiselect :value="openMin" :options="minuteOptions" open-direction="below" :show-labels="false" :allow-empty="false" placeholder="MM" @input="(v) => openMin = v" />
                        </div>
                      </b-col>
                      <b-col cols="12" md="6">
                        <small class="text-muted d-block mb-1">{{ $t('store.close') }}</small>
                        <div class="d-flex align-items-center" style="gap: 6px;">
                          <multiselect :value="closeHour" :options="hourOptions" open-direction="below" :show-labels="false" :allow-empty="false" placeholder="HH" @input="(v) => closeHour = v" />
                          <span class="font-weight-bold">:</span>
                          <multiselect :value="closeMin" :options="minuteOptions" open-direction="below" :show-labels="false" :allow-empty="false" placeholder="MM" @input="(v) => closeMin = v" />
                        </div>
                      </b-col>
                    </b-row>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.status')">
                    <multiselect
                      :value="selectedStatus"
                      :options="statusOptions"
                      track-by="value"
                      label="text"
                      open-direction="below"
                      :show-labels="false"
                      :allow-empty="false"
                      :placeholder="$t('label.select')"
                      @input="onStatusChange"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" class="text-right">
                  <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                  <b-button type="submit" variant="primary" :disabled="submitting">
                    {{ $t('label.submit') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
